/**
 * Live server-event bus (PWA).
 *
 * WebSocket client for ppGateway's /ws/events endpoint. ppServer forwards the
 * same data-change notifications the desktop client receives (via its
 * ServerEventMapping) and the gateway fans them out to subscribers whose
 * outlet scope matches. Open views use this bus to auto-refresh when the
 * record they display changes:
 *
 *   serverEvents.on('stock', (ev) => { if (ev.value === myId) reload(); });
 *
 * Message shapes from the gateway (JSON text frames):
 *   { type, typeName, code, key, value, serverId, store }  — data-change event
 *   { event: 'resync' | 'server_online' | 'server_offline', serverId }
 *       — lifecycle hints. 'resync' follows a WebSocket reconnect or a
 *         ppServer reconnect (views should refresh). 'server_online' /
 *         'server_offline' announce tunnel servers coming/going and are
 *         broadcast to EVERY connection (availability indicators update).
 *   { op: 'subscribed' | 'unsubscribed' | 'pong' }         — command acks
 *   { error: '...' }                                       — command failure
 *
 * The client is intentionally resilient: when the gateway (or ppServer) is
 * unavailable it retries with exponential backoff and the rest of the PWA
 * keeps working unchanged.
 */

import { getIdToken } from './firebase.js';

export type ServerEvent = {
  type: number;
  /** Event category name: 'stock', 'membership', 'company', 'customer', 'sales', ... */
  typeName: string;
  /** Operation: 'updated', 'deleted', 'undeleted', 'inserted', 'updated-list', ... */
  code: string;
  /** Meaning of `value`: 'id', 'date', ... */
  key: string;
  /** Record id (usually), epoch date for '*-list' codes — compare with displayed ids. */
  value: string;
  serverId: string;
  store: string;
};

export type LifecycleEvent = { event: string; serverId?: string };
export type ServerEventListener = (ev: ServerEvent) => void;
export type LifecycleListener = (ev: LifecycleEvent) => void;

const PING_INTERVAL_MS = 25_000;
const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

/**
 * Event codes that mean the underlying record/list changed. Panels use this to
 * ignore UI-only hints (`print_order`, …) that never alter the rows they show.
 * Same codes the desktop ServerEventMapping reacts to for list reloads.
 */
export const LIST_CHANGE_CODES: ReadonlySet<string> = new Set([
  'updated',
  'updated-list',
  'insert',
  'inserted',
  'deleted',
  'undeleted',
]);

/** Trailing debounce helper (used by panels to coalesce event bursts). */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number,
): ((...args: A) => void) & { cancel(): void } {
  let timer: number | null = null;
  const wrapped = (...args: A) => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = null;
      fn(...args);
    }, ms);
  };
  return Object.assign(wrapped, {
    cancel: () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    },
  });
}

class ServerEvents {
  private ws: WebSocket | null = null;
  private sessionKey = '';
  private subscribing: { storeCodes: string[] } | null = null;
  private listeners = new Map<string, Set<ServerEventListener>>();
  private lifecycleListeners = new Set<LifecycleListener>();
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private backoff = BACKOFF_MIN_MS;
  private wantConnected = false;

  // ── Lifecycle ────────────────────────────────────────────────────────

  /** Call after login when the gateway session key is available. */
  connect(sessionKey: string): void {
    this.sessionKey = sessionKey;
    this.wantConnected = true;
    this.open();
  }

  /** Call on logout — closes the socket and clears subscriptions. */
  disconnect(): void {
    this.wantConnected = false;
    this.subscribing = null;
    this.stopTimers();
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Subscribe to live events for the given outlets (replaces any previous
   * subscription). The UI may display several stores at once (Stock Search),
   * so all of the user's registered outlets are subscribed in one command.
   * Safe to call before the socket is open — it is sent on connect/reconnect.
   */
  subscribe(storeCodes: string[]): void {
    this.subscribing = { storeCodes: [...storeCodes] };
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendSubscribe();
    }
  }

  /** Drop the current subscription (e.g. back to the store list). */
  unsubscribe(): void {
    this.subscribing = null;
    this.sendRaw({ op: 'unsubscribe' });
  }

  // ── Listener API ─────────────────────────────────────────────────────

  /**
   * Subscribe to a specific event typeName ('stock', 'membership', 'company',
   * 'customer', 'salesrep', 'promoter', ...) or '*' for every event.
   * Returns an unsubscribe function.
   */
  on(typeName: string, handler: ServerEventListener): () => void {
    let set = this.listeners.get(typeName);
    if (!set) {
      set = new Set();
      this.listeners.set(typeName, set);
    }
    set.add(handler);
    return () => {
      set?.delete(handler);
    };
  }

  /**
   * Lifecycle events:
   *  - 'resync': connection (re)established or ppServer reconnected — views
   *    may have missed events, refresh what is displayed.
   *  - 'server_offline': the outlet's ppServer went away (data is stale).
   *  - 'server_online': a tunnel server (re)connected — availability UIs
   *    should re-resolve which outlets it serves.
   */
  onLifecycle(handler: LifecycleListener): () => void {
    this.lifecycleListeners.add(handler);
    return () => {
      this.lifecycleListeners.delete(handler);
    };
  }

  // ── Internals ────────────────────────────────────────────────────────

  private open(): void {
    if (!this.wantConnected || !this.sessionKey) return;
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    const proto = location.protocol === 'https:' ? 'wss://' : 'ws://';
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${proto}${location.host}/ws/events`);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.backoff = BACKOFF_MIN_MS;
      // A (re)connected socket may have missed events, and the first connect
      // after a page load carries a sessionStorage snapshot that may predate a
      // server-side change (expiry renewal, role/outlet changes). Always ask
      // views to resync so they re-read state instead of trusting that cache.
      this.emitLifecycle({ event: 'resync' });
      if (this.subscribing) this.sendSubscribe();
      this.startPing();
    };

    ws.onmessage = (msg) => {
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(String(msg.data)) as Record<string, unknown>;
      } catch {
        return;
      }
      if (data.error) {
        // Stale session (gateway restart) → reconnect carries a fresh idToken.
        if (data.error === 'unauthorized') {
          try {
            ws.close();
          } catch {
            /* ignore */
          }
        }
        return;
      }
      if (typeof data.event === 'string') {
        this.emitLifecycle(data as unknown as LifecycleEvent);
        return;
      }
      if (typeof data.typeName === 'string') {
        this.emit(data as unknown as ServerEvent);
      }
      // {op:'pong'|'subscribed'} acks are ignored.
    };

    ws.onclose = () => {
      this.stopPing();
      if (this.ws === ws) this.ws = null;
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      /* onclose follows and drives the retry */
    };
  }

  private sendSubscribe(): void {
    if (!this.subscribing) return;
    const storeCodes = [...this.subscribing.storeCodes];
    void (async () => {
      let idToken = '';
      try {
        idToken = (await getIdToken()) || '';
      } catch {
        /* proceed without — server-key auth may still work */
      }
      // Bail if the subscription changed while the token was refreshing.
      if (this.subscribing?.storeCodes.join('|') !== storeCodes.join('|')) return;
      this.sendRaw({ op: 'subscribe', sk: this.sessionKey, stores: storeCodes, idToken });
    })();
  }

  private sendRaw(payload: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(payload));
    } catch {
      /* ignore — reconnect path handles it */
    }
  }

  private startPing(): void {
    this.stopPing();
    // Ping once up front so the gateway registers this connection immediately
    // (outlet availability broadcasts reach every connection — even one with
    // no store subscriptions yet).
    this.sendRaw({ op: 'ping' });
    this.pingTimer = window.setInterval(() => {
      this.sendRaw({ op: 'ping' });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private stopTimers(): void {
    this.stopPing();
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (!this.wantConnected || this.reconnectTimer !== null) return;
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, BACKOFF_MAX_MS);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private emit(ev: ServerEvent): void {
    this.listeners.get(ev.typeName)?.forEach((fn) => {
      try {
        fn(ev);
      } catch {
        /* listener errors must not break the bus */
      }
    });
    this.listeners.get('*')?.forEach((fn) => {
      try {
        fn(ev);
      } catch {
        /* ignore */
      }
    });
  }

  private emitLifecycle(ev: LifecycleEvent): void {
    this.lifecycleListeners.forEach((fn) => {
      try {
        fn(ev);
      } catch {
        /* ignore */
      }
    });
  }
}

export const serverEvents = new ServerEvents();
